import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from '@/portal/components/ProtectedRoute';
import LoginGate from '@/portal/components/LoginGate';
import { useTheme } from '@/hooks/useTheme';
import { useLanguage } from '@/hooks/useLanguage';
import { useUIStore } from '@/stores/uiStore';
import Preloader from '@/components/layout/Preloader';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import Toast from '@/components/feedback/Toast';
import Home from '@/pages/Home/Home';
import About from '@/pages/About/About';
import Services from '@/pages/Services/Services';
import ServiceDetail from '@/pages/Services/ServiceDetail';
import Tests from '@/pages/Tests/Tests';
import Branches from '@/pages/Branches/Branches';
import Blog from '@/pages/Blog/Blog';
import BlogPost from '@/pages/Blog/BlogPost';
import Contact from '@/pages/Contact/Contact';
import Careers from '@/pages/Careers/Careers';
import MedicalProfiles from '@/pages/MedicalProfiles/MedicalProfiles';
import Technology from '@/pages/Technology/Technology';
import VirtualTour from '@/pages/VirtualTour/VirtualTour';
import HomeVisitRequest from '@/pages/HomeVisitRequest/HomeVisitRequest';

const PortalLayout = lazy(() => import('@/portal/components/PortalLayout'));
const LoginPage = lazy(() => import('@/portal/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/portal/pages/DashboardPage'));
const VisitsPage = lazy(() => import('@/portal/pages/VisitsPage'));
const VisitDetailsPage = lazy(() => import('@/portal/pages/VisitDetailsPage'));
const ResultsPage = lazy(() => import('@/portal/pages/ResultsPage'));
const ProfilePage = lazy(() => import('@/portal/pages/ProfilePage'));
const PointsPage = lazy(() => import('@/portal/pages/PointsPage'));
const ComplaintsPage = lazy(() => import('@/portal/pages/ComplaintsPage'));
const DoctorLayout = lazy(() => import('@/portal/doctor/components/DoctorLayout'));
const DoctorDashboardPage = lazy(() => import('@/portal/doctor/pages/DoctorDashboardPage'));
const DoctorResultsPage = lazy(() => import('@/portal/doctor/pages/DoctorResultsPage'));
const DoctorSettingsPage = lazy(() => import('@/portal/doctor/pages/DoctorSettingsPage'));

function App() {
  const { theme } = useTheme();
  const { language } = useLanguage();
  const { isPreloaderVisible, setPreloaderVisible } = useUIStore();

  useEffect(() => {
    // Hide preloader after initial load
    const timer = setTimeout(() => {
      setPreloaderVisible(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [setPreloaderVisible]);

  return (
    <div className={`app theme-${theme} lang-${language}`}>
      {isPreloaderVisible && <Preloader />}
      <Header />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/tests" element={<Tests />} />
          <Route path="/branches" element={<Branches />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/medical-profiles" element={<MedicalProfiles />} />
          <Route path="/technology" element={<Technology />} />
          <Route path="/virtual-tour" element={<VirtualTour />} />
          <Route path="/home-visit-request" element={<HomeVisitRequest />} />
          <Route
            path="/login"
            element={
              <LoginGate>
                <Suspense fallback={null}>
                  <LoginPage />
                </Suspense>
              </LoginGate>
            }
          />
          <Route
            path="/account"
            element={
              <LoginGate>
                <ProtectedRoute>
                  <Suspense fallback={null}>
                    <PortalLayout />
                  </Suspense>
                </ProtectedRoute>
              </LoginGate>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={null}>
                  <DashboardPage />
                </Suspense>
              }
            />
            <Route
              path="visits"
              element={
                <Suspense fallback={null}>
                  <VisitsPage />
                </Suspense>
              }
            />
            <Route
              path="visits/:visitId"
              element={
                <Suspense fallback={null}>
                  <VisitDetailsPage />
                </Suspense>
              }
            />
            <Route
              path="results"
              element={
                <Suspense fallback={null}>
                  <ResultsPage />
                </Suspense>
              }
            />
            <Route
              path="points"
              element={
                <Suspense fallback={null}>
                  <PointsPage />
                </Suspense>
              }
            />
            <Route
              path="complaints"
              element={
                <Suspense fallback={null}>
                  <ComplaintsPage />
                </Suspense>
              }
            />
            <Route
              path="profile"
              element={
                <Suspense fallback={null}>
                  <ProfilePage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="/doctor"
            element={
              <LoginGate>
                <ProtectedRoute role="doctor">
                  <Suspense fallback={null}>
                    <DoctorLayout />
                  </Suspense>
                </ProtectedRoute>
              </LoginGate>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={null}>
                  <DoctorDashboardPage />
                </Suspense>
              }
            />
            <Route
              path="results"
              element={
                <Suspense fallback={null}>
                  <DoctorResultsPage />
                </Suspense>
              }
            />
            <Route
              path="settings"
              element={
                <Suspense fallback={null}>
                  <DoctorSettingsPage />
                </Suspense>
              }
            />
          </Route>
        </Routes>
      </main>
      <Footer />
      <Toast />
    </div>
  );
}

export default App;

